import { useCallback, useEffect, useRef, useState } from "react";
import { Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography as T,
} from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import useErrorToast from "~/contexts/error-toast";
import SamplePromptRow from "./SamplePromptRow";

const { VITE_CATALOG_API } = import.meta.env;

type SaveState = "idle" | "saving" | "saved";

const MAX_PROMPTS = 6;
const DEBOUNCE_MS = 800;

const SamplePromptsTab = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [prompts, setPrompts] = useState<string[]>(() => dataset?.samplePrompts ?? []);
  const [saveStates, setSaveStates] = useState<SaveState[]>(() =>
    (dataset?.samplePrompts ?? []).map(() => "idle" as SaveState)
  );

  const confirmedRef = useRef<string[]>(dataset?.samplePrompts ?? []);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Sync from server when dataset loads/changes externally
  useEffect(() => {
    if (dataset?.samplePrompts) {
      const server = dataset.samplePrompts;
      // Only sync if the confirmed ref matches what we last saved —
      // meaning there's no pending local edit
      if (JSON.stringify(confirmedRef.current) !== JSON.stringify(server)) {
        setPrompts(server);
        setSaveStates(server.map(() => "idle" as SaveState));
        confirmedRef.current = server;
      }
    }
  }, [dataset?.samplePrompts]);

  const save = useCallback(
    async (toSave: string[], changedIndices: number[]) => {
      if (!dataset) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setSaveStates((prev) => {
        const next = [...prev];
        for (const i of changedIndices) {
          if (i < next.length) next[i] = "saving";
        }
        return next;
      });

      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}/sample-prompts`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompts: toSave }),
          signal: controller.signal,
        });

        if (!resp.ok) throw new Error("Failed to save sample prompts");

        confirmedRef.current = toSave;

        setSaveStates((prev) => {
          const next = [...prev];
          for (const i of changedIndices) {
            if (i < next.length) next[i] = "saved";
          }
          return next;
        });

        mutate((current) => {
          if (!current || current.id !== dataset.id) return current;
          return { ...current, samplePrompts: toSave };
        });
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        console.error(e);
        showError("Unable to save sample prompts");
        // Reset saving states back to idle on error
        setSaveStates((prev) => {
          const next = [...prev];
          for (const i of changedIndices) {
            if (i < next.length) next[i] = "idle";
          }
          return next;
        });
      }
    },
    [dataset, mutate, showError]
  );

  const debounceSave = useCallback(
    (newPrompts: string[]) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      const confirmed = confirmedRef.current;
      const changedIndices: number[] = [];
      for (let i = 0; i < newPrompts.length; i++) {
        if (newPrompts[i] !== confirmed[i]) changedIndices.push(i);
      }

      if (changedIndices.length === 0) return;

      timerRef.current = setTimeout(() => {
        save(newPrompts, changedIndices);
      }, DEBOUNCE_MS);
    },
    [save]
  );

  const handleChange = (index: number, value: string) => {
    const updated = [...prompts];
    updated[index] = value;
    setPrompts(updated);

    // Clear any saved/saving indicator when typing
    setSaveStates((prev) => {
      const next = [...prev];
      next[index] = "idle";
      return next;
    });

    debounceSave(updated);
  };

  const handleAdd = () => {
    if (prompts.length >= MAX_PROMPTS) return;
    const updated = [...prompts, ""];
    setPrompts(updated);
    setSaveStates((prev) => [...prev, "idle"]);
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (deleteIndex === null || !dataset) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    const updated = prompts.filter((_, i) => i !== deleteIndex);

    setDeleting(true);
    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}/sample-prompts`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompts: updated }),
      });

      if (!resp.ok) throw new Error("Failed to delete sample prompt");

      confirmedRef.current = updated;
      setPrompts(updated);
      setSaveStates((prev) => prev.filter((_, i) => i !== deleteIndex));
      mutate((current) => {
        if (!current || current.id !== dataset.id) return current;
        return { ...current, samplePrompts: updated };
      });
      setDeleteIndex(null);
    } catch (e) {
      console.error(e);
      showError("Unable to delete sample prompt");
    }
    setDeleting(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return (
    <Stack gap={2}>
      <T variant="body2" color="text.secondary">
        Write up to 6 example prompts that demonstrate what users can ask and do with this dataset.
        These will appear at the start of new agent conversations.
      </T>
      <Stack gap={2}>
        {prompts.map((prompt, index) => (
          <SamplePromptRow
            key={index}
            index={index}
            value={prompt}
            saveState={saveStates[index] ?? "idle"}
            disabled={!isOwner}
            onChange={(value) => handleChange(index, value)}
            onDelete={() => setDeleteIndex(index)}
          />
        ))}
      </Stack>
      {isOwner && prompts.length < MAX_PROMPTS && (
        <Box>
          <Button
            startIcon={<Add />}
            onClick={handleAdd}
            size="small"
            sx={{ backgroundColor: "grey.200", "&:hover": { backgroundColor: "grey.300" } }}
          >
            Add Prompt
          </Button>
        </Box>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={deleteIndex !== null}
        onClose={() => !deleting && setDeleteIndex(null)}
      >
        <DialogTitle>Delete Prompt</DialogTitle>
        <DialogContent sx={{ fontSize: 14 }}>
          Are you sure you want to delete this sample prompt?
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" disabled={deleting} onClick={() => setDeleteIndex(null)}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={deleting}
            onClick={confirmDelete}
            sx={{
              backgroundColor: "attention.main",
              "&:hover": { backgroundColor: "attention.dark" },
            }}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SamplePromptsTab;
